import { useEffect, useRef, useState } from 'react';

type UseLinearProgressParams = {
    isProcessing: boolean;
    updatedAt?: string | number | Date;
    /** Real server-side estimate (seconds). When valid (finite & > 0) it drives the bar and `hasEstimate` is true. */
    runtimeEstimatedTime?: number;
    /** Used when no real estimate is available yet. */
    fallbackSeconds?: number;
    capPercent?: number; // default 99
    tickMs?: number; // default 1000ms — bar uses CSS transition, countdown text is mm:ss
};

export function useLinearProgress({
    isProcessing,
    updatedAt,
    runtimeEstimatedTime,
    fallbackSeconds = 60,
    capPercent = 99,
    tickMs = 1000,
}: UseLinearProgressParams) {
    const hasEstimate = Number.isFinite(Number(runtimeEstimatedTime)) && Number(runtimeEstimatedTime) > 0;
    const estimatedSeconds = hasEstimate ? Number(runtimeEstimatedTime) : fallbackSeconds;

    const [progress, setProgress] = useState(0);
    const [remainingMs, setRemainingMs] = useState(0);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        if (isProcessing) {
            try {
                const parsed = typeof updatedAt === 'string' || typeof updatedAt === 'number'
                    ? Date.parse(String(updatedAt))
                    : (updatedAt instanceof Date ? updatedAt.getTime() : NaN);
                const startMs = Number.isFinite(parsed) ? Number(parsed) : Date.now();
                const totalMs = Math.max(1, Math.floor((Number(estimatedSeconds) > 0 ? Number(estimatedSeconds) : 60) * 1000));
                const cap = Math.max(0, Math.min(100, Math.floor(Number(capPercent) || 99)));
                const computePct = (elapsed: number) => {
                    const raw = (elapsed / totalMs) * cap;
                    return Math.max(0, Math.min(cap, Math.round(raw * 100) / 100));
                };
                const tick = () => {
                    const elapsed = Date.now() - startMs;
                    setProgress(computePct(elapsed));
                    setRemainingMs(Math.max(0, totalMs - elapsed));
                };
                tick();
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
                timerRef.current = window.setInterval(tick, Math.max(50, Number(tickMs) || 1000));
            } catch { }
            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
            };
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setProgress(0);
            setRemainingMs(0);
        }
    }, [isProcessing, updatedAt, estimatedSeconds, capPercent, tickMs]);

    const remainingText = (() => {
        try {
            const totalSeconds = Math.ceil(Math.max(0, Number(remainingMs)) / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            const mm = String(minutes).padStart(2, '0');
            const ss = String(seconds).padStart(2, '0');
            return `${mm}:${ss}`;
        } catch {
            return '—';
        }
    })();

    return { progress, remainingMs, remainingText, hasEstimate } as const;
}
